import { yupResolver } from '@hookform/resolvers/yup';
import { IonButton, IonButtons, IonCol, IonContent, IonHeader, IonIcon, IonItem, IonList, IonRow, IonText, IonTextarea, IonTitle, IonToggle, IonToolbar } from '@ionic/react';
import { arrowBack } from 'ionicons/icons';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';

import { useEffect, useState } from 'react';
import { logTransaction } from '../../../common/log';
import { LogType } from '../../../models/log.model';
import { useToast } from '../../../providers/ToastProvider';
import store from '../../../store';
import { useAppDispatch } from '../../../store/hooks';
import { addTechnicianCommentAsync } from '../../../store/technicianCommentSlice';
import { IonModalWrapper } from './AddTechnicianCommentModal.styles';

export interface IComponentProps {
  showModal: boolean;
  handleDismiss?: () => void;
}

interface IFormInputs {
  techComment: string;
  isAlert: boolean;
}

yup.addMethod(yup.string, 'commentRequired', function (errorMessage) {
  return this.test(`test-comment`, errorMessage, function (value: any) {
    const { path, createError } = this;
    if (!value || !value.trim()) {
      return createError({ path, message: errorMessage });
    }
    return true;
  });
});

const schema = yup.object().shape({
  techComment: (yup.string() as any).commentRequired('provide a comment'),
});

const AddTechnicianCommentModal: React.FC<IComponentProps> = function ({ showModal, handleDismiss }) {
  let submitButtonRef: HTMLButtonElement | null = null;
  const [isAlert, setIsAlert] = useState(false);
  const {
    actions: { errorToast },
  } = useToast();

  const dispatch = useAppDispatch();

  useEffect(() => {
    if (showModal) {
      reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModal]);

  const FormTextArea = (control: any, name: string, maxLength: number, label: string, className?: string) => {
    return (
      <Controller
        control={control}
        name={name}
        render={({ field: { onChange, value }, fieldState: { error } }) => {
          return (
            <>
              <IonTextarea
                label={label}
                labelPlacement="stacked"
                value={value}
                onIonInput={(e) => {
                  onChange(e.detail.value);
                }}
                className={className}
                maxlength={maxLength}
                autoGrow
              />
              {error && <IonText className="error">{error.message}</IonText>}
            </>
          );
        }}
      />
    );
  };

  const FormToggle = (control: any, name: string, label: string, handleChange?: (val: boolean) => void) => {
    return (
      <Controller
        control={control}
        name={name}
        render={({ field: { onChange, value }, fieldState: { error } }) => {
          return (
            <IonToggle
              color="primarylight"
              labelPlacement="start"
              className="width-100pc"
              onIonChange={(e) => {
                onChange(e.detail.checked);
                handleChange && handleChange(e.detail.checked);
              }}
              checked={value}
              slot="end">
              {label}
            </IonToggle>
          );
        }}
      />
    );
  };

  const { control, handleSubmit, reset } = useForm<IFormInputs>({
    defaultValues: {
      techComment: '',
      isAlert: false,
    },
    mode: 'onChange',
    resolver: yupResolver(schema),
  });

  const onSubmit = handleSubmit(async (data) => {
    try {
      const { labourClocking } = store.getState().technicianClocking;

      logTransaction(LogType.Info, `Add tech comment requested`);
      if (labourClocking?.jobData) {
        handleDismiss && handleDismiss();
        await dispatch(
          addTechnicianCommentAsync({
            repairOrderNumber: labourClocking.jobData.repairOrderNumber,
            branchCode: labourClocking.jobData.branchCode,
            jobCode: labourClocking.jobData.jobCode,
            jobType: labourClocking.jobData.jobType,
            comment: data.techComment,
            isAlert: data.isAlert,
            isAdhoc: labourClocking.jobData.isAdhoc,
          }),
        );
      } else {
        throw new Error('Failed to save technician comment, no job data available');
      }
    } catch (ex: any) {
      logTransaction(LogType.Error, `${ex?.message}`);
      errorToast(ex?.message);
    }
  });

  return (
    <IonModalWrapper
      className="stacked-modal"
      isOpen={showModal}
      onDidDismiss={() => {
        if (showModal) {
          handleDismiss && handleDismiss();
        }
      }}>
      <IonHeader color="lightest">
        <IonToolbar color="primary">
          <IonButtons slot="start">
            <IonButton color="primary" fill="solid" onClick={() => handleDismiss && handleDismiss()}>
              <IonIcon icon={arrowBack} />
            </IonButton>
          </IonButtons>
          <IonButtons slot="end">
            <IonButton color="primary" fill="solid" onClick={() => submitButtonRef?.click()}>
              Save
            </IonButton>
          </IonButtons>
          <IonTitle>Add Technician Comment</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent color="lightest" className="ion-padding">
        <form id="addTechCommentForm" noValidate onSubmit={onSubmit}>
          <button
            type="submit"
            className="ion-hide"
            ref={(button) => {
              if (!submitButtonRef && button) {
                submitButtonRef = button;
              }
            }}>
            Submit
          </button>
          <IonList>
            <IonItem>
              <IonRow className="width-100pc">
                <IonCol className="ion-no-padding">{FormTextArea(control, 'techComment', 8192, `Technician ${isAlert ? 'Alert' : 'Comment'}`, 'tech-comment')}</IonCol>
                <IonCol size="auto">
                  <div className="flex-checkbox">{FormToggle(control, 'isAlert', 'Alert', (val: boolean) => setIsAlert(val))}</div>
                </IonCol>
              </IonRow>
            </IonItem>
          </IonList>
        </form>
      </IonContent>
    </IonModalWrapper>
  );
};

export default AddTechnicianCommentModal;
